import { HpHelpInfo, HpHelpInfoConditionalText, HpHelpInfoItem } from '@avantia/questionset-model';
import DOMPurify from 'dompurify';
import { HpHelpInfoUI } from '../../types';
import { HpGetHelpAndInfoTextResult, HpInfoPositionType } from '../../types/widgetUtilitiesTypes';

const getHelpAndInfoResultOptionalProps: (keyof HpGetHelpAndInfoTextResult)[] = [
  'helpText',
  'infoPosition',
  'infoText',
  'warningText',
  'isInfoIconHidden',
  'isHelpVisible',
  'warningText'
];

export function getHelpAndInfoText(helpInfo?: HpHelpInfo, ui?: HpHelpInfoUI): HpGetHelpAndInfoTextResult {
  const { helpText, infoText, infoPosition, warningText, isInfoIconHidden } = getUiOrQuestionHelpText(helpInfo, ui);
  const { isHelpVisible, canShowHelp: showHelp, canShowInfo: showInfo, canShowWarning: showWarning } = ui || {};
  const canShowHelp: boolean = showHelp !== false && !!helpText;
  const canShowInfo: boolean = showInfo !== false && !!infoText;
  const canShowWarning: boolean = showWarning !== false && !!warningText;
  const res: HpGetHelpAndInfoTextResult = {
    canShowHelp,
    canShowInfo,
    canShowWarning,
    helpText: isHelpVisible === true && canShowHelp ? helpText : undefined,
    isHelpVisible: canShowHelp && isHelpVisible ? true : undefined,
    infoText: canShowInfo ? infoText : undefined,
    infoPosition: canShowInfo ? infoPosition || 'bottom' : undefined,
    warningText: canShowWarning ? warningText : undefined,
    isInfoIconHidden
  };

  getHelpAndInfoResultOptionalProps.forEach((prop) => {
    if (res[prop] === undefined) {
      delete res[prop];
    }
  });

  return res;
}

interface GetHelpTextResult {
  helpText: string | undefined;
  infoPosition?: HpInfoPositionType;
  infoText: string | undefined;
  isInfoIconHidden?: boolean | undefined;
  warningText: string | undefined;
}

function getUiOrQuestionHelpText(helpInfo?: HpHelpInfo, ui?: HpHelpInfoUI): GetHelpTextResult {
  const helpText = getText('helpText', helpInfo, ui);
  const warningText = getText('warningText', helpInfo, ui);
  const { infoPosition, infoText, isInfoIconHidden } = getInfoText(helpInfo, ui);
  return { helpText, infoText, infoPosition, warningText, isInfoIconHidden };
}

function getText(key: keyof HpHelpInfoUI, helpInfo?: HpHelpInfo, ui?: HpHelpInfoUI): string | undefined {
  const uiText: string | undefined = ui ? (ui[key] as string | undefined) : undefined;
  const item: HpHelpInfoItem | undefined = helpInfo ? helpInfo[key] : undefined;
  if (!uiText && item && typeof item === 'object') {
    const { text } = item as HpHelpInfoConditionalText;
    return text;
  }

  return uiText || (item as string);
}

interface HpGetInfoTextResult {
  infoPosition: HpInfoPositionType;
  infoText: string;
  isInfoIconHidden: boolean | undefined;
}

function getInfoText(helpInfo?: HpHelpInfo, ui?: HpHelpInfoUI): Partial<HpGetInfoTextResult> {
  const { infoText, infoPosition, isInfoIconHidden } = ui || {};
  if (infoText) {
    return { infoText, infoPosition, isInfoIconHidden };
  }

  const { infoText: item } = helpInfo || {};
  if (!infoText && item && typeof item === 'object') {
    const { text: infoText, position, isIconHidden } = item as HpHelpInfoConditionalText;
    return { infoText, infoPosition: position, isInfoIconHidden: isIconHidden };
  }

  return { infoText: item as string };
}

const additionalAllowedAttributes: string[] = ['target', 'open', 'align'];

export function sanitiseHtmlContent(html: string): string {
  return DOMPurify.sanitize(html, { ADD_ATTR: additionalAllowedAttributes });
}
